/**
 * Socket Service - Centralized Socket.IO client management
 * Handles authentication and connection management
 */

import { io, Socket } from 'socket.io-client'
import { useAuthStore } from '@/stores/authStore'
import { useChatStore } from '@/stores/chatStore'
import type { SocketEvents } from '@/types/chat'

class SocketService {
  private socket: Socket | null = null
  private isConnecting = false
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 1000

  /**
   * Initialize socket connection with authentication
   */
  async connect(): Promise<Socket> {
    if (this.socket?.connected) {
      return this.socket
    }

    if (this.isConnecting) {
      // Wait for existing connection attempt
      return new Promise((resolve, reject) => {
        const checkConnection = () => {
          if (this.socket?.connected) {
            resolve(this.socket)
          } else if (!this.isConnecting) {
            reject(new Error('Connection failed'))
          } else {
            setTimeout(checkConnection, 100)
          }
        }
        checkConnection()
      })
    }

    this.isConnecting = true

    try {
      const authStore = useAuthStore()
      const token = authStore.getToken

      if (!token) {
        throw new Error('No authentication token available')
      }

      const baseURL = import.meta.env.VITE_STRAPI_URL || 'http://localhost:1337'

      this.socket = io(baseURL, {
        auth: {
          token
        },
        transports: ['websocket', 'polling'],
        timeout: 20000,
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: this.reconnectDelay
      })

      // Set up event listeners
      this.setupEventListeners()

      // Wait for connection
      await new Promise<void>((resolve, reject) => {
        if (!this.socket) return reject(new Error('Socket not initialized'))

        this.socket.on('connect', () => {
          this.reconnectAttempts = 0
          resolve()
        })

        this.socket.on('connect_error', (error) => {
          reject(error)
        })
      })

      return this.socket
    } catch (error) {
      this.isConnecting = false
      throw error
    } finally {
      this.isConnecting = false
    }
  }

  /**
   * Disconnect from socket
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
    }
    this.isConnecting = false
    this.reconnectAttempts = 0
  }

  /**
   * Get current socket instance
   */
  getSocket(): Socket | null {
    return this.socket
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false
  }

  /**
   * Emit event to server
   */
  emit<K extends keyof SocketEvents>(
    event: K, 
    data: Parameters<SocketEvents[K]>[0]
  ): void {
    if (!this.socket?.connected) {
      return
    }

    this.socket.emit(event, data)
  }

  /**
   * Listen for event from server
   */
  on<K extends keyof SocketEvents>(
    event: K, 
    handler: SocketEvents[K]
  ): void {
    if (!this.socket) {
      return
    }

    this.socket.on(event as string, handler)
  }

  /**
   * Remove event listener
   */
  off<K extends keyof SocketEvents>(
    event: K, 
    handler?: SocketEvents[K]
  ): void {
    if (!this.socket) return

    if (handler) {
      this.socket.off(event as string, handler)
    } else {
      this.socket.off(event as string)
    }
  }


  /**
   * Send a message
   */
  sendMessage(receiverId: string | number, text?: string, attachmentId?: number): void {
    if (!this.isConnected()) {
      return
    }
    
    // Backend doesn't use conversationId - it creates/finds conversation internally
    const messageData = { receiverId: Number(receiverId), text, attachmentId }
    this.emit('sendMessage', messageData)
  }

  /**
   * Mark messages as read
   */
  markMessagesAsRead(chatPartnerId: string | number): void {
    this.emit('markMessagesAsRead', { chatPartnerId })
  }

  /**
   * Start typing indicator
   */
  startTyping(chatPartnerId: string | number): void {
    this.emit('userTypingStart', { chatPartnerId })
  }

  /**
   * Stop typing indicator
   */
  stopTyping(chatPartnerId: string | number): void {
    this.emit('userTypingStop', { chatPartnerId })
  }

  /**
   * Setup socket event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) return

    this.socket.on('connect', () => {
      // Connected
    })

    this.socket.on('disconnect', (reason) => {
      
      // Attempt to reconnect if disconnected unexpectedly
      if (reason === 'io server disconnect') {
        // Server initiated disconnect, don't reconnect
        return
      }
      
      this.handleReconnection()
    })

    this.socket.on('connect_error', () => {
      this.handleReconnection()
    })

    this.socket.on('newMessage', (data: { conversationDocumentId: string; message: unknown }) => {
      // New message event - increment unread count
      const chatStore = useChatStore()
      if (data.conversationDocumentId) {
        chatStore.incrementUnreadCount(data.conversationDocumentId, 1)
      }
    })

    this.socket.on('newMessageNotification', (data: { conversationDocumentId: string; unreadCount: number }) => {
      // New message notification event with unread count
      const chatStore = useChatStore()
      if (data.conversationDocumentId && typeof data.unreadCount === 'number') {
        chatStore.setUnreadCount(data.conversationDocumentId, data.unreadCount)
      }
    })

    this.socket.on('messagesRead', (data: { conversationDocumentId: string }) => {
      // Messages read event - reset unread count
      const chatStore = useChatStore()
      if (data.conversationDocumentId) {
        chatStore.markConversationAsRead(data.conversationDocumentId)
      }
    })

    this.socket.on('chatError', (error: { message: string }) => {
      console.error('💥 Chat error from server:', error)
      const chatStore = useChatStore()
      chatStore.handleSocketError(error.message)
      
      // Re-emit for components to handle
      if (this.socket) {
        this.socket.emit('chatErrorHandled', error)
      }
    })

    this.socket.on('org:online', () => {
      // Organization online event
    })

    this.socket.on('org:offline', () => {
      // Organization offline event
    })

    this.socket.on('orgs:status', () => {
      // Organizations status update event
    })

    this.socket.on('partnerTypingStart', () => {
      // Partner typing start event
    })

    this.socket.on('partnerTypingStop', () => {
      // Partner typing stop event
    })
  }

  /**
   * Handle reconnection logic
   */
  private handleReconnection(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return
    }

    this.reconnectAttempts++
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1) // Exponential backoff


    setTimeout(() => {
      if (!this.socket?.connected) {
        this.connect().catch(() => {
          // Reconnection failed
        })
      }
    }, delay)
  }

}

// Create singleton instance
export const socketService = new SocketService()
export default socketService